import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  activitiesData: [],
  selectedEventId: null,
  loading: false,
  error: null,
  saving: false,
  refreshing: false,
  lastFetched: null,
  pagination: {
    page: 1,
    page_size: 50,
    total_count: 0,
    total_pages: 0,
  },
};

export const vcxEventActivitiesSlice = createSlice({
  name: 'vcxEventActivities',
  initialState,
  reducers: {
    // Set all activities data
    setActivitiesData: (state, action) => {
      // Ensure we always set an array
      state.activitiesData = Array.isArray(action.payload) ? action.payload : [];
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Set paginated activities data
    setPaginatedActivitiesData: (state, action) => {
      const { activities, pagination } = action.payload;
      state.activitiesData = activities;
      state.pagination = pagination;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Add a new activity
    addActivity: (state, action) => {
      const newActivity = action.payload;
      // Prepend to array (most recent first)
      state.activitiesData.unshift(newActivity);
      state.pagination.total_count += 1;
    },

    // Update an existing activity
    updateActivity: (state, action) => {
      const updatedActivity = action.payload;
      const index = state.activitiesData.findIndex(a => a.id === updatedActivity.id);
      if (index !== -1) {
        state.activitiesData[index] = { ...state.activitiesData[index], ...updatedActivity };
      }
    },

    // Delete activity
    deleteActivity: (state, action) => {
      const activityId = action.payload;
      state.activitiesData = state.activitiesData.filter(a => a.id !== activityId);
      state.pagination.total_count = Math.max(0, state.pagination.total_count - 1);
    },

    // Set selected event ID
    setSelectedEventId: (state, action) => {
      state.selectedEventId = action.payload;
      // Clear activities when switching events
      if (state.selectedEventId !== action.payload) {
        state.activitiesData = [];
        state.pagination = initialState.pagination;
      }
    },

    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Clear all activities (when closing sidebar, etc.)
    clearActivities: (state) => {
      state.activitiesData = [];
      state.selectedEventId = null;
      state.pagination = initialState.pagination;
    },
  },
});

export const {
  setActivitiesData,
  setPaginatedActivitiesData,
  addActivity,
  updateActivity,
  deleteActivity,
  setSelectedEventId,
  setPagination,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
  clearActivities,
} = vcxEventActivitiesSlice.actions;

// Selectors
export const selectActivitiesData = (state) => state.vcxEventActivities.activitiesData;
export const selectSelectedEventId = (state) => state.vcxEventActivities.selectedEventId;
export const selectPagination = (state) => state.vcxEventActivities.pagination;
export const selectLoading = (state) => state.vcxEventActivities.loading;
export const selectRefreshing = (state) => state.vcxEventActivities.refreshing;
export const selectSaving = (state) => state.vcxEventActivities.saving;
export const selectError = (state) => state.vcxEventActivities.error;
export const selectLastFetched = (state) => state.vcxEventActivities.lastFetched;

export default vcxEventActivitiesSlice.reducer;
